import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { cancelRecoveryByToken } from "../../api/recovery.js";
import { describeAuthError } from "../../app/authFlow.js";
import { paths } from "../../app/paths.js";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { Logo } from "../../ui/Logo.js";
import { Alert, Skeleton, buttonClass } from "../../ui/primitives.js";

type Outcome =
  | { readonly kind: "pending" }
  | { readonly kind: "done"; readonly status: string }
  | { readonly kind: "failed"; readonly message: string };

const TOKEN = /^[A-Za-z0-9_-]{43}$/;

function describeStatus(status: string): string {
  switch (status) {
    case "cancelled":
      return "Die Wiederherstellung deines Kontos wurde abgebrochen. Dein Master-Passwort und deine Anmeldung bleiben unverändert.";
    case "completed":
      return "Die Wiederherstellung wurde bereits ausgeführt. Melde dich bei einem Administrator, falls du sie nicht angefordert hast.";
    default:
      return "Diese Anfrage ist nicht mehr offen. Es muss nichts weiter getan werden.";
  }
}

/** Public landing page for the cancel link in the owner's recovery mail. */
export function CancelRecoveryPage(): ReactNode {
  useDocumentTitle("Wiederherstellung abbrechen");
  const [outcome, setOutcome] = useState<Outcome>({ kind: "pending" });

  useEffect((): void => {
    const token = window.location.hash.replace(/^#/, "");
    if (!TOKEN.test(token)) {
      setOutcome({ kind: "failed", message: "Der Link ist unvollständig. Öffne ihn bitte direkt aus der E-Mail." });
      return;
    }
    // Keep the token out of the address bar and history once it is read.
    window.history.replaceState(null, "", window.location.pathname);
    cancelRecoveryByToken(token)
      .then((status: string): void => setOutcome({ kind: "done", status }))
      .catch((err: unknown): void => setOutcome({ kind: "failed", message: describeAuthError(err) }));
  }, []);

  return (
    <main className="flex min-h-dvh items-center justify-center px-5 py-12 sm:px-10">
      <div className="w-full max-w-[26rem]">
        <div className="mb-10">
          <Logo />
        </div>
        <div className="flex flex-col gap-5 animate-fade-up">
          <h1 className="text-xl font-semibold text-foam">Wiederherstellung abbrechen</h1>
          {outcome.kind === "pending" ? <Skeleton className="h-16" /> : null}
          {outcome.kind === "done" ? (
            <Alert tone={outcome.status === "completed" ? "danger" : "info"}>{describeStatus(outcome.status)}</Alert>
          ) : null}
          {outcome.kind === "failed" ? <Alert tone="danger">{outcome.message}</Alert> : null}
          <Link to={paths.login} className={buttonClass("ghost")}>Zur Anmeldung</Link>
        </div>
      </div>
    </main>
  );
}
